import { map, pipe } from 'rxjs';
import { RequestStatus } from './types';
import { DataType } from 'python-struct';
import { ModesOfOperation } from './cia402';
import { fromQ } from './util';

/**
 * Type guard to determine if the provided value is a OS Command Response object.
 */
export function isOsCommandResponse(value: any): value is OsCommandResponse {
  return (value as OsCommandResponse)?.command instanceof Uint8Array;
}

export interface OsCommandResponse {
  /**
   * The command that was sent to the device through the 0x1023:01 OS Command.
   */
  command?: Uint8Array;

  /**
   * Array of numbers representing the received data.
   *
   * The length of data depends on the response type.
   * If the response number is 3 (Command completed with error and with response),
   * the length can be up to 5 numbers; otherwise, it can be up to 6 numbers.
   */
  data?: number[];

  /**
   * The error code is only set when the response number is 3 (Command completed with error and with response).
   *
   * The meaning of the error code can be general or command specific.
   * General error codes start at 254 and go downwards:
   * - 254: Unsupported command
   * - 253: Command timeout
   * - 252: Command aborted
   * - 251: Command not allowed
   * OS command specific error codes start at 0 and go upwards.
   */
  errorCode?: number;

  /**
   * The error name is derived from the OS command number and error code number.
   */
  errorName?: string;

  /**
   * The error description is derived from the OS command number and error code number.
   * The purpose of the error description is to provide more information about the error than the name can.
   */
  errorDescription?: string;

  /**
   * Stores the content of the fs-buffer file if accessed during the execution of the OS command.
   */
  fsBuffer?: Uint8Array | number[] | null | undefined;

  /**
   * Reported progress during the OS command execution.
   *
   * The progress can be in the range from 0 to 100 and is computed from the command status.
   * A status value between 100 and 200 indicates that the command is executing with a percentage (100 = 0%, 200 = 100%).
   */
  progress?: number;

  /**
   * Status of the request.
   */
  request: RequestStatus;

  /**
   * Raw response as sent by the device.
   */
  response: Uint8Array;
}

export function parseOsCommandResponse(response: Uint8Array, command?: Uint8Array): OsCommandResponse {
  const status = response.at(0);

  if (status === undefined) {
    throw new Error(`No status is available in the typed array response ${JSON.stringify(response)}`);
  }

  let data: number[] | undefined;
  let errorCode: number | undefined;
  let errorName: string | undefined;
  let errorDescription: string | undefined;
  let progress: number | undefined;
  let request: RequestStatus = 'started';

  // Determine the status of the OS command.
  if (status === 0 || status === 1) {
    request = 'succeeded';
  } else if (status === 2 || status === 3) {
    request = 'failed';
  } else if ((status >= 100 && status <= 200) || status === 255) {
    request = 'running';
  } else {
    throw new Error(`Unknown OS command response status: ${status}`);
  }

  // Command in progress with percentage.
  if (status >= 100 && status <= 200) {
    progress = status - 100;
  }

  // Command completed with error and with response.
  if (status === 3) {
    errorCode = response.at(2);
    if (errorCode !== undefined) {
      errorName = osCommandErrorCode[errorCode];
    }
  }

  if (status === 1) {
    // 6 bytes for data when last command completed, no errors, response data available
    data = Array.from(response.slice(2));
  } else if (status === 3) {
    // 5 bytes for data when last command completed, error, response data available
    data = Array.from(response.slice(3));
  }

  return { command, data, errorCode, errorName, errorDescription, progress, request, response };
}

export enum OsCommandMode {
  EXECUTE_THE_NEXT_COMMAND_IMMEDIATELY = 0,
  ABORT_THE_CURRENT_COMMAND_AND_ALL_COMMANDS_IN_THE_BUFFER = 3,
}

/**
 * Outcome of aborting the OS command in progress.
 *
 * See {@link MotionMasterReqResClient#abortOsCommand}.
 */
export interface OsCommandAbortResult {
  /**
   * `succeeded` when the drive no longer reports a command in progress, `failed` when the command was
   * still running once the timeout elapsed. The OS command mode is reset to
   * {@link OsCommandMode.EXECUTE_THE_NEXT_COMMAND_IMMEDIATELY} in both cases.
   */
  request: RequestStatus;

  /**
   * Whether a command was in progress when the abort was requested. When `false`, the abort was a no-op
   * for the drive, and the reported `last*` properties belong to the previously completed command, if any.
   */
  wasInProgress: boolean;

  /**
   * Raw status byte (0x1023:03 Byte 0) of the last response read from the drive.
   *
   * An aborted command typically reports 3 (command completed with error and with response) together with
   * `lastErrorCode` 252 (command aborted).
   */
  lastStatus?: number;

  /**
   * OS error code of the last response read from the drive; see {@link OsCommandResponse#errorCode}.
   */
  lastErrorCode?: number;

  /**
   * Name of {@link OsCommandAbortResult#lastErrorCode}, when known.
   */
  lastErrorName?: string;

  /**
   * Set when the command was still in progress once the timeout elapsed.
   */
  timeout?: boolean;
}

export enum OsCommandId {
  CUSTOM_OS_COMMAND = -1,
  ENCODER_REGISTER_COMMUNICATION = 0,
  ICMU_CALIBRATION_MODE = 1,
  OPEN_LOOP_FIELD_MODE = 2,
  HRD_STREAMING = 3,
  MOTOR_PHASE_ORDER_DETECTION = 4,
  COMMUTATION_OFFSET_MEASUREMENT = 5,
  OPEN_PHASE_DETECTION = 6,
  POLE_PAIR_DETECTION = 7,
  PHASE_RESISTANCE_MEASUREMENT = 8,
  PHASE_INDUCTANCE_MEASUREMENT = 9,
  TORQUE_CONSTANT_MEASUREMENT = 10,
  SMM_ACYCLIC_HANDLER = 11,
  SKIPPED_CYCLED_COUNTER = 13,
  IGNORE_BISS_STATUS_BITS = 14,
  SYSTEM_IDENTIFICATION = 15,
  TRIGGER_ERROR = 16,
  KUBLER_ENCODER_COMMAND = 17,
  USE_INTERNAL_ENCODER_VELOCITY = 18,
  KUBLER_ENCODER_REGISTER_COMMUNICATION = 19,
}

export type OsCommandErrors = { [key: number]: { errorName: string; errorDescription: string } };

export interface OsCommandSubcommand<T> {
  id: T;
  name: string;
}

export enum SmmAcyclicHandlerSubcommandId {
  PARAMETER_DOWNLOAD_LOGIN = 1,
  TRANSMIT_PARAMETERS = 2,
  LOAD_PARAMETERS_FOR_VERIFICATION = 3,
  VERIFY_PARAMETERS = 4,
  LOAD_VALIDATION_FILE = 5,
  VALIDATE_CONFIGURATION = 6,
  CHANGE_PASSWORD = 7,
  LOGOUT = 8,
  SOFTWARE_UPDATE_LOGIN = 9,
  CONFIGURE_SOFTWARE_UPDATE = 10,
  TRANSMIT_SOFTWARE = 11,
  FINALIZE_SOFTWARE_UPDATE = 12,
  READ_SMM_PARAMETER = 13,
  IO_FAILURE_ACKNOWLEDGE = 14,
  TRIGGER_RESTART = 15,
}

export const osCommandErrorCode: { [key: number]: string } = {
  251: 'Command not allowed',
  252: 'Command aborted',
  253: 'Command timeout',
  254: 'Command unsupported',
};

export function createOsCommandTypedArray(elements: number[]): Uint8Array {
  return new Uint8Array(elements.concat(new Array(8 - elements.length).fill(0)));
}

/**
 * OS command 0: Encoder register communication
 *
 * This OS command is utilized for reading or writing registers from an encoder service.
 * Presently, the only service supporting this functionality is BiSS, but it can be easily extended to other encoder services.
 * For more information on how BiSS register communication operates, please consult the official BiSS C Interface Protocol Description.
 *
 * |        | Bit 7 | Bit 6 | Bit 5 | Bit 4 | Bit 3 | Bit 2 | Bit 1 | Bit 0 |
 * |--------|:-----:|:-----:|:-----:|:-----:|:-----:|:-----:|:-----:|:-----:|
 * | Byte 0 |   0   |   0   |   0   |   0   |   0   |   0   |   0   |   0   |
 * | Byte 1 |   -   |   -   |   -   |   -   |   -   |   A   |   A   |   A   |
 * | Byte 2 |   B   |   B   |   B   |   B   |   B   |   B   |   B   |   C   |
 * | Byte 3 |   D   |   D   |   D   |   D   |   D   |   D   |   D   |   D   |
 * | Byte 4 |   E   |   E   |   E   |   E   |   E   |   E   |   E   |   E   |
 * | Byte 5 |   -   |   -   |   -   |   -   |   -   |   -   |   -   |   -   |
 * | Byte 6 |   -   |   -   |   -   |   -   |   -   |   -   |   -   |   -   |
 * | Byte 7 |   -   |   -   |   -   |   -   |   -   |   -   |   -   |   -   |
 *
 * @param encoderOrdinal (A) 2^3=8 encoder ordinals. Counting starts from 1, so '1' refers to Encoder 1 configured in 0x2110.
 * @param slaveAddress (B) 2^7=128 slave addresses. Select the address of the slave to read (BiSS protocol level). Since our BiSS service doesn't support multiple slaves in the same device, this value should always be 0.
 * @param rw (C) Read or write the encoder register. Select the direction of the command: 0 for reading a register, and 1 for writing it.
 * @param registerAddress (D) Register to be read/written.
 * @param registerWriteValue (E) The value to write to the register. This value is ignored when reading a register (when rw is set to 0).
 */
export function createEncoderRegisterCommunicationOsCommand(
  encoderOrdinal: number = 1,
  slaveAddress: number = 0,
  rw: number = 0,
  registerAddress: number = 0,
  registerWriteValue: number = 0
): Uint8Array {
  const byte2 = (slaveAddress << 1) | rw;
  const elements: number[] = [
    OsCommandId.ENCODER_REGISTER_COMMUNICATION,
    encoderOrdinal,
    byte2,
    registerAddress,
    registerWriteValue,
  ];

  return createOsCommandTypedArray(elements);
}

export interface EncoderRegisterCommunicationOsCommandResponse extends OsCommandResponse {
  registerValue?: number;
}

/**
 * OS command 1: iC-MU calibration mode
 *
 * This command is used to set a particular BiSS mode when using an iC-MU encoder, which is primarily employed for iC-MU calibration.
 *
 * The supported modes are explained below:
 *
 * **Configuration mode**: The BiSS service clocks the encoder normally, but it only utilizes the bits for register communication and discards the rest,
 * thereby not updating the encoder data.
 * This also implies that the BisCrc error is not triggered while in this mode.
 * This mode is intended to allow the user to change the configuration in the encoder BiSS registers without encountering a BisCrc error.
 *
 * **Raw mode**: The BiSS service clocks the encoder assuming it was correctly configured for raw mode.
 * It continues to calculate all the encoder data, but additionally, it averages the raw data and makes it available in 0x2704 User MISO, as follows:
 *
 * <table border="1">
 *   <tr>
 *     <td>31</td>
 *     <td>30</td>
 *     <td>29</td>
 *     <td>28</td>
 *     <td>27</td>
 *     <td>26</td>
 *     <td>25</td>
 *     <td>24</td>
 *     <td>23</td>
 *     <td>22</td>
 *     <td>21</td>
 *     <td>20</td>
 *     <td>19</td>
 *     <td>18</td>
 *     <td>17</td>
 *     <td>16</td>
 *     <td>15</td>
 *     <td>14</td>
 *     <td>13</td>
 *     <td>12</td>
 *     <td>11</td>
 *     <td>10</td>
 *     <td>9</td>
 *     <td>8</td>
 *     <td>7</td>
 *     <td>6</td>
 *     <td>5</td>
 *     <td>4</td>
 *     <td>3</td>
 *     <td>2</td>
 *     <td>1</td>
 *     <td>0</td>
 *   </tr>
 *   <tr>
 *     <td colspan="4" style="text-align: center;">0</td>
 *     <td colspan="14" style="text-align: center;">Nonius count</td>
 *     <td colspan="14" style="text-align: center;">Master count</td>
 *   </tr>
 * </table>
 *
 * This data can also be read by using OS command 3.
 *
 * **Standard mode**: The BiSS service clocks the encoder normally.
 *
 * When transitioning from any mode to Configuration mode, the current position is saved.
 * If we subsequently switch to Raw mode, this saved position serves as the starting point for position calculation because the raw data is relative.
 * Therefore, the motor should not be moved while in Configuration mode if there are plans to switch to Raw mode afterward.
 *
 * @param encoderOrdinal (A) 2^3=8 encoder ordinals. Counting starts from 1, so '1' refers to Encoder 1 configured in 0x2110.
 * @param modeEnable: Select the iC-MU calibration mode. Configuration mode is 0, raw mode is 1, and standard mode is 2.
 */
export function createIcMuCalibrationModeOsCommand(encoderOrdinal: number = 1, modeEnable: number = 0) {
  const byte2 = (modeEnable << 3) | encoderOrdinal;
  const elements: number[] = [OsCommandId.ICMU_CALIBRATION_MODE, byte2];
  return createOsCommandTypedArray(elements);
}

export interface IcMuCalibrationModeOsCommandResponse extends OsCommandResponse {
  calibrationModeSet?: boolean;
}

export function createOpenLoopFieldModeOsCommand(parameterIndex: number = 0, parameterValue: number = 0) {
  let msb: number = (parameterValue >> 24) & 0xff;
  let msb1: number = (parameterValue >> 16) & 0xff;
  let msb2: number = (parameterValue >> 8) & 0xff;
  let lsb: number = parameterValue & 0xff;

  const elements: number[] = [OsCommandId.OPEN_LOOP_FIELD_MODE, parameterIndex, msb, msb1, msb2, lsb];

  return createOsCommandTypedArray(elements);
}

export const openLoopFieldModeParameters = [
  { parameterIndex: 0, parameterName: 'Starting electrical angle [mrad]' },
  { parameterIndex: 1, parameterName: 'Ending electrical angle [mrad]' },
  { parameterIndex: 2, parameterName: 'Maximum electrical rotational speed [rad / s]' },
  { parameterIndex: 3, parameterName: 'Electrical rotational acceleration [rad / s²]' },
  { parameterIndex: 4, parameterName: 'Starting length [per thousand rated current]' },
  { parameterIndex: 5, parameterName: 'Ending length [per thousand rated current]' },
  { parameterIndex: 6, parameterName: 'Increasing length rate [per thousand rated current / s]' },
];

export interface OpenLoopFieldModeOsCommandResponse extends OsCommandResponse {
  openLoopFieldMode?: boolean;
}

/**
 * Enum for selecting the action to perform in HRD streaming.
 */
export enum HrdStreamingOsCommandAction {
  /**
   * Deletes all previous HRD files from the filesystem.
   * The user then configures the stream duration and the variable to stream.
   * File deletion may take up to approximately 5 seconds in the worst-case scenario.
   */
  CONFIGURE_STREAM,

  /**
   * Starts the stream using the previously configured settings.
   */
  START_STREAM,
}

/**
 * Enum for selecting the data to stream.
 *
 * This value is used only when the selected Action is Configure Stream (0).
 */
export enum HrdStreamingOsCommandDataIndex {
  /**
   * Record raw encoder data.
   *
   * The encoder must be set up first using OS command 1.
   */
  ENCODER_RAW_DATA,

  /**
   * Record system identification data.
   *
   * The identification procedure must be configured with OS command 15 and started before recording.
   */
  SYSTEM_IDENTIFICATION_DATA,
}

export function createHrdStreamingOsCommand(
  action: HrdStreamingOsCommandAction = 0,
  dataIndex: HrdStreamingOsCommandDataIndex = 0,
  duration: number = 10000
) {
  let msb = (duration >> 8) & 0xff;
  let lsb = duration & 0xff;
  const elements = [OsCommandId.HRD_STREAMING, action, dataIndex, msb, lsb];
  return createOsCommandTypedArray(elements);
}

export interface HrdStreamingOsCommandResponse extends OsCommandResponse {
  hrdStreamingComplete: true;
}

export const hrdStreamingOsCommandErrors: OsCommandErrors = {
  0: {
    errorName: 'Initialization error',
    errorDescription: 'The command failed to initialize',
  },
  1: {
    errorName: 'Streaming error',
    errorDescription: 'Something failed while streaming was in progress',
  },
  2: {
    errorName: 'Duration value',
    errorDescription: 'The selected Duration value is larger than 10000ms',
  },
  3: {
    errorName: 'Data index value',
    errorDescription: 'The selected Data index is unknown',
  },
  4: {
    errorName: 'Action value',
    errorDescription: 'The selected Action is unknown',
  },
};

/**
 * Parses a binary HRD data buffer into an array of velocity/torque samples.
 *
 * Each sample in the buffer consists of:
 * - 4 bytes: velocity (signed 32-bit integer, little-endian, Q15 fixed-point format)
 * - 2 bytes: torque (signed 16-bit integer, little-endian)
 *
 * The function iterates through the buffer in 6-byte chunks, extracting
 * velocity and torque for each sample. The velocity is converted from
 * Q15 fixed-point format to a floating-point number and **rounded to an integer**.
 *
 * Each returned sample is a tuple of the form:
 * `[velocityQ15, torque, velocity]`, where:
 * - `velocityQ15` is the original 32-bit Q15 integer from the buffer
 * - `torque` is the 16-bit signed integer from the buffer
 * - `velocity` is the floating-point velocity value converted from Q15 and rounded to an integer
 *
 * If the buffer length is not a multiple of 6, a warning is logged and any trailing bytes are discarded.
 *
 * @param data - A `Uint8Array` containing raw HRD binary data.
 * @returns An array of `[velocityQ15, torque, velocity]` tuples one per sample.
 */
export function parseHrdBuffer(data: Uint8Array): number[][] {
  const bytesPerSample = 6;

  const validLength = data.byteLength - (data.byteLength % bytesPerSample);

  if (validLength !== data.byteLength) {
    console.warn(
      `HRD buffer length ${data.byteLength} is not a multiple of ${bytesPerSample}. ` +
        `Discarding ${data.byteLength - validLength} trailing byte(s).`
    );
  }

  const samples: number[][] = [];
  const view = new DataView(data.buffer, data.byteOffset, validLength);

  let offset = 0;
  while (offset < validLength) {
    const velocityQ15 = view.getInt32(offset, true); // 4 bytes little-endian, Q15 format
    const torque = view.getInt16(offset + 4, true); // 2 bytes little-endian
    const velocity = fromQ(velocityQ15, 15, true); // converted from Q15 and rounded to an integer

    samples.push([velocityQ15, torque, velocity]);
    offset += bytesPerSample;
  }

  return samples;
}

/**
 * Converts a 2D array of HRD (High Resolution Data) samples into a CSV-formatted string.
 *
 * @param samples - A two-dimensional array where each inner array represents a single sample
 *   in the format `[velocityQ15, torque, velocity]`.
 * @returns A CSV string with a header row and one row per sample.
 *          Returns an empty string if no samples are provided.
 *
 * @example
 * ```ts
 * const samples = [
 *   [12345, 1.23, 100.5],
 *   [12346, 1.20, 101.2]
 * ];
 *
 * const csv = convertHrdSamplesToCsv(samples);
 * // "HRD Velocity (Q15),HRD Torque [‰],HRD Velocity [RPM]\n12345,1.23,100.5\n12346,1.20,101.2\n"
 * ```
 *
 * @remarks
 * - The CSV header is fixed as: `"HRD Velocity (Q15),HRD Torque [‰],HRD Velocity [RPM]"`.
 * - Each sample is written on a new line, with comma-separated values.
 */
export function convertHrdSamplesToCsv(samples: number[][]): string {
  if (samples.length === 0) {
    return '';
  }

  const header = 'HRD Velocity (Q15),HRD Torque [‰],HRD Velocity [RPM]\n';
  const rows = samples.map(([velocityQ15, torque, velocity]) => {
    return `${velocityQ15},${torque},${velocity}\n`;
  });

  return header + rows.join('');
}

/**
 * Command 4: Motor phase order detection
 *
 * This command is used to detect the order of the motor phases.
 * When this command finishes successfully, the found value is automatically written to the object 0x2003:5 Motor phases inverted.
 * This command rotates the motor.
 *
 * This OS command has no arguments.
 *
 * When the command finishes successfully, it will return a value of 0 for normal and 1 for inverted motor phases.
 *
 * This command doesn't have any command-specific OS error code, so this status will only happen when a general OS error code occurs.
 *
 * This command will report its progress without indicating a percentage.
 *
 * This command can only be run when the opmode is -2 (OPMODE_DIAGNOSTICS), the CIA402 state is Operation enabled, there is no active limit switch,
 * and the brake status is disengaged (in case there is a configured brake). If any of these conditions is not true during the execution of the command,
 * the execution will stop, and it will return the OS error code 251 (command not allowed).
 */
export function createMotorPhaseOrderDetectionOsCommand() {
  const elements: number[] = [OsCommandId.MOTOR_PHASE_ORDER_DETECTION];
  return createOsCommandTypedArray(elements);
}

export interface MotorPhaseOrderDetectionOsCommandResponse extends OsCommandResponse {
  motorPhaseOrder?: number;
}

/**
 * Command 5: Commutation offset measurement
 *
 * This command is used to measure the commutation angle offset.
 * The configuration of the procedure is derived from the pre-existing and previously used commutation offset objects in the object dictionary.
 * Upon successful completion of this command, the determined value is automatically written to:
 * - 0x2001:00 Commutation angle offset
 * - 0x2009:01 State is set to OFFSET_VALID
 *
 * In version 5.0, three different methods can be selected for command 5. These methods are named:
 * - Commutation offset measurement - method 0
 * - Commutation offset measurement - method 1
 * - Commutation offset measurement - method 2
 * Among these three methods, "method 0" and "method 1" involve rotating the rotor.
 *
 * This command can only be run when the opmode is -2 (OPMODE_DIAGNOSTICS) and the CIA402 state is Operation enabled.
 * Additionally, if the commutation offset measurement method (0x2009:3) is set to 0 or 1,
 * the command is permissible only when there are no active limit switches, and the brake status is disengaged (in case a brake is configured).
 * If any of these conditions is not met during the execution of the command, the execution will be halted,
 * and it will return the OS error code 251 (Command not allowed).
 */
export function createCommutationOffsetMeasurementOsCommand() {
  const elements: number[] = [OsCommandId.COMMUTATION_OFFSET_MEASUREMENT];
  return createOsCommandTypedArray(elements);
}

export interface CommutationOffsetMeasurementOsCommandResponse extends OsCommandResponse {
  commutationAngleOffset?: number;
}

/**
 * Command 6: Open phase detection
 *
 * This command is used to detect whether one of the phases is open.
 * It might rotate the motor if there is no brake or if it is disengaged.
 *
 * If this command finishes successfully, it indicates that all motor phases seem to be fine.
 *
 * This command can only be run when the opmode is -2 (OPMODE_DIAGNOSTICS), the CIA402 state is Operation enabled,
 * and there is no limit switch active. If any of these conditions is not true during the execution of the command,
 * the execution will stop, and it will return the OS error code 251 (Command not allowed).
 */
export function createOpenPhaseDetectionOsCommand() {
  const elements: number[] = [OsCommandId.OPEN_PHASE_DETECTION];
  return createOsCommandTypedArray(elements);
}

export interface OpenPhaseDetectionOsCommandResponse extends OsCommandResponse {
  phaseOpened?: boolean;
}

export const openPhaseDetectionErrors: OsCommandErrors = {
  0: {
    errorName: 'Open terminal A',
    errorDescription: 'Terminal A of the Drive is not connected',
  },
  1: {
    errorName: 'Open terminal B',
    errorDescription: 'Terminal B of the Drive is not connected',
  },
  2: {
    errorName: 'Open terminal C',
    errorDescription: 'Terminal C of the Drive is not connected',
  },
  3: {
    errorName: 'Open FET A high',
    errorDescription: 'Upper FET in leg A of the Drive is not conducting (open circuit fault)',
  },
  4: {
    errorName: 'Open FET A low',
    errorDescription: 'Lower  FET in leg A of the Drive is not conducting (open circuit fault)',
  },
  5: {
    errorName: 'Open FET B high',
    errorDescription: 'Upper FET in leg B of the Drive is not conducting (open circuit fault)',
  },
  6: {
    errorName: 'Open FET B low',
    errorDescription: 'Lower  FET in leg B of the Drive is not conducting (open circuit fault)',
  },
  7: {
    errorName: 'Open FET C high',
    errorDescription: 'Upper FET in leg C of the Drive is not conducting (open circuit fault)',
  },
  8: {
    errorName: 'Open FET C low',
    errorDescription: 'Lower  FET in leg C of the Drive is not conducting (open circuit fault)',
  },
};

/**
 * Command 7: Pole pair detection
 *
 * This command is used to detect the number of pole pairs that the connected motor has.
 * It requires the rotation of the rotor.
 *
 * This command can only be run when the opmode is -2 (OPMODE_DIAGNOSTICS), the CIA402 state is Operation enabled,
 * and there is no limit switch active. If any of these conditions is not true during the execution of the command,
 * the execution will stop, and it will return the OS error code 251 (Command not allowed).
 */
export function createPolePairDetectionOsCommand() {
  const elements: number[] = [OsCommandId.POLE_PAIR_DETECTION];
  return createOsCommandTypedArray(elements);
}

export interface PolePairDetectionOsCommandResponse extends OsCommandResponse {
  numberOfPolePairs?: number;
}

/**
 * Command 8: Phase resistance measurement
 *
 * This command is used to measure the phase resistance of the motor in milliohms (mΩ).
 * It might rotate the motor if there is no brake or if it is disengaged.
 *
 * This command can only be run when the opmode is -2 (OPMODE_DIAGNOSTICS), the CIA402 state is Operation enabled,
 * and there is no limit switch active. If any of these conditions is not true during the execution of the command,
 * the execution will stop, and it will return the OS error code 251 (Command not allowed).
 */
export function createPhaseResistanceMeasurementOsCommand() {
  const elements: number[] = [OsCommandId.PHASE_RESISTANCE_MEASUREMENT];
  return createOsCommandTypedArray(elements);
}

export interface PhaseResistanceMeasurementOsCommandResponse extends OsCommandResponse {
  phaseResistance?: number;
}

/**
 * Command 9: Phase inductance measurement
 *
 * This command is used to measure the phase inductance of the motor in microhenries (μH).
 * It might rotate the motor if there is no brake or if it is disengaged.
 *
 * This command can only be run when the opmode is -2 (OPMODE_DIAGNOSTICS), the CIA402 state is Operation enabled,
 * and there is no limit switch active. If any of these conditions is not true during the execution of the command,
 * the execution will stop, and it will return the OS error code 251 (Command not allowed).
 */
export function createPhaseInductanceMeasurementOsCommand() {
  const elements: number[] = [OsCommandId.PHASE_INDUCTANCE_MEASUREMENT];
  return createOsCommandTypedArray(elements);
}

export interface PhaseInductanceMeasurementOsCommandResponse extends OsCommandResponse {
  phaseInductance?: number;
}

export function createTorqueConstantMeasurementOsCommand() {
  const elements: number[] = [OsCommandId.TORQUE_CONSTANT_MEASUREMENT];
  return createOsCommandTypedArray(elements);
}

export interface TorqueConstantMeasurementOsCommandResponse extends OsCommandResponse {
  torqueConstant: number;
}

export function createSmmAcyclicHandlerOsCommand(subcommandId: SmmAcyclicHandlerSubcommandId) {
  const elements: number[] = [OsCommandId.SMM_ACYCLIC_HANDLER, subcommandId];
  return createOsCommandTypedArray(elements);
}

export function createSmmAcyclicHandlerReadSmmParameterOsCommand(index: number, subindex: number = 0) {
  let indexMsb: number = (index >> 8) & 0xff;
  let indexLsb: number = index & 0xff;
  const subindexByte = subindex & 0xff;
  const elements: number[] = [
    OsCommandId.SMM_ACYCLIC_HANDLER,
    SmmAcyclicHandlerSubcommandId.READ_SMM_PARAMETER,
    indexLsb,
    indexMsb,
    subindexByte,
  ];
  return createOsCommandTypedArray(elements);
}

export function createSmmAcyclicHandlerConfigureSoftwareUpdateOsCommand(fileSize: number) {
  const b2 = (fileSize >> 0) & 0xff;
  const b3 = (fileSize >> 8) & 0xff;
  const b4 = (fileSize >> 16) & 0xff;
  const b5 = (fileSize >> 24) & 0xff;
  const elements: number[] = [
    OsCommandId.SMM_ACYCLIC_HANDLER,
    SmmAcyclicHandlerSubcommandId.CONFIGURE_SOFTWARE_UPDATE,
    b2,
    b3,
    b4,
    b5,
  ];
  return createOsCommandTypedArray(elements);
}

/**
 * Error codes of OS command 11 (SMM acyclic handler), reported in byte 2 of the response when the status is 3.
 *
 * The SoC side of the handler (`sc_drive/module_os_command/src/oscmd_smm_acyclic_handler.xc`) currently emits only
 * 15 (timeout) and 16 (action not supported) with a code. When the SMM itself rejects a request (wrong password, no
 * open session, ...) the status is 2 and no code is sent, so the reason cannot be told apart from the response alone.
 * Codes 0-9 are the ones defined by the firmware test helpers (`somanet_oscmd_helpers.py`, `OsCmd11ErrorCodes`) and
 * are decoded in case a firmware reports them.
 */
export const smmAcyclicHandlerOsCommandErrors: OsCommandErrors = {
  0: { errorName: 'File not found', errorDescription: 'The fs-buffer file required by the command was not found.' },
  1: { errorName: 'Login failed', errorDescription: 'The SMM rejected the login.' },
  2: { errorName: 'Transmission failed', errorDescription: 'Transmitting the data to the SMM failed.' },
  3: { errorName: 'Requesting parameters failed', errorDescription: 'Requesting the parameters from the SMM failed.' },
  4: { errorName: 'Verification failed', errorDescription: 'The SMM rejected the parameter verification.' },
  5: {
    errorName: 'Requesting report failed',
    errorDescription: 'Requesting the validation report from the SMM failed.',
  },
  6: { errorName: 'Validation failed', errorDescription: 'The SMM rejected the configuration validation.' },
  7: { errorName: 'Change password failed', errorDescription: 'The SMM rejected the password change.' },
  8: { errorName: 'Logout failed', errorDescription: 'The SMM rejected the logout.' },
  9: { errorName: 'Parameter read failed', errorDescription: 'Reading the parameter from the SMM failed.' },
  15: {
    errorName: 'Timeout',
    errorDescription: 'The SMM did not answer within the time the drive allows for this command.',
  },
  16: { errorName: 'Action not supported', errorDescription: 'The drive firmware does not support this SMM command.' },
};

/**
 * Describes why an OS command failed, in a form that can be appended to an error message.
 *
 * Status 2 (completed with error, no reply) carries no error code. For the SMM acyclic handler this is how the SMM
 * reports a rejected request. Status 3 carries an error code, which is named when it is known.
 *
 * @param response - The final response of the OS command.
 * @returns A short description such as `the SMM rejected the request (status 2)` or `Timeout (error code 15): ...`.
 */
export function describeOsCommandFailure(response: OsCommandResponse): string {
  const status = response.response?.at(0);

  if (typeof response.errorCode === 'number') {
    const name = response.errorName ?? 'Unknown error';
    const description = response.errorDescription ? `: ${response.errorDescription.replace(/\.$/, '')}` : '';
    return `${name} (error code ${response.errorCode})${description}`;
  }

  if (response.command?.[0] === OsCommandId.SMM_ACYCLIC_HANDLER) {
    return `the SMM rejected the request (status ${status})`;
  }

  return `the command completed with an error (status ${status})`;
}

export interface SmmAcyclicHandlerOsCommandResponse extends OsCommandResponse {
  parameterValueUpdated?: boolean;
}

export interface SmmAcyclicHandlerLoadParametersForVerificationOsCommandResponse extends OsCommandResponse {
  values: DataType[];
}

export function createSkippedCyclesCounterOsCommand(firmwareService: number = 0) {
  const elements: number[] = [OsCommandId.SKIPPED_CYCLED_COUNTER, firmwareService];
  return createOsCommandTypedArray(elements);
}

export interface SkippedCyclesCounterOsCommandResponse extends OsCommandResponse {
  skippedCycles: number;
}

export function createIgnoreBissStatusBitsOsCommand(trigger: number = 1, encoderOrdinal: number = 1) {
  const byte2 = (encoderOrdinal << 1) | trigger;
  const elements: number[] = [OsCommandId.IGNORE_BISS_STATUS_BITS, byte2];
  return createOsCommandTypedArray(elements);
}

export interface IgnoreBissStatusBitsOsCommandResponse extends OsCommandResponse {
  ignoringBissStatusBits: boolean;
}

export function createSystemIdentificationOsCommand(parameterIndex: number = 0, parameterValue: number = 0) {
  let msb: number = (parameterValue >> 24) & 0xff;
  let msb1: number = (parameterValue >> 16) & 0xff;
  let msb2: number = (parameterValue >> 8) & 0xff;
  let lsb: number = parameterValue & 0xff;
  const elements: number[] = [OsCommandId.SYSTEM_IDENTIFICATION, parameterIndex, msb, msb1, msb2, lsb];
  return createOsCommandTypedArray(elements);
}

export enum SystemIdentificationOsCommandSignalType {
  LOGARITHMIC_WITH_AMPLITUDE_MODULATION = 0,
  LINEAR_WITH_CONSTANT_AMPLITUDE = 1,
}

export enum SystemIdentificationOsCommandStartProcedure {
  NO_ACTION = 0,
  IMMEDIATELLY = 1,
  WAIT_FOR_HRD_STREAMING_TO_START = 2,
}

/**
 * Defines the parameters used to execute a chirp signal on a device.
 *
 * A chirp signal is a frequency sweep that transitions linearly from a
 * starting frequency to a target frequency over a specified time period,
 * with a defined amplitude. These options control how the chirp signal
 * is generated and executed.
 */
export type ChirpSignalOptions = {
  /**
   * Duration of high-rate data streaming (in milliseconds) that runs in parallel
   * with the chirp excitation. This streaming window is used to collect
   * high-resolution measurement data required for plant model computation.
   */
  hrdStreamingDuration: number;

  /**
   * Modes of operation to set before starting the chirp signal procedure.
   * If not provided, the current modes of operation will be used.
   *
   * Sets the value of 0x6060: Modes of operation before starting the procedure.
   */
  modesOfOperation?: ModesOfOperation;

  /**
   * Type of chirp signal to be used.
   *
   * This is parameter number 4 in the system identification OS command.
   */
  signalType: SystemIdentificationOsCommandSignalType;

  /**
   * Start frequency of the chirp signal, in millihertz (mHz).
   *
   * This is parameter number 0 in the system identification OS command.
   */
  startFrequency: number;

  /**
   * When to start the chirp signal procedure.
   *
   * This is parameter number 5 in the system identification OS command.
   */
  startProcedure: SystemIdentificationOsCommandStartProcedure;

  /**
   * Target amplitude of the chirp signal, expressed in permils (‰)
   * of the rated torque.
   *
   * This is parameter number 2 in the system identification OS command.
   */
  targetAmplitude: number;

  /**
   * Target frequency of the chirp signal, in millihertz (mHz).
   *
   * This is parameter number 1 in the system identification OS command.
   */
  targetFrequency: number;

  /**
   * Duration of the frequency sweep, in milliseconds (ms),
   * representing the transition time from start to target frequency.
   *
   * This is parameter number 3 in the system identification OS command.
   */
  transitionTime: number;
};

export const systemIdentificationParameters = [
  { parameterIndex: 0, parameterName: 'Start frequency [mHz]' },
  { parameterIndex: 1, parameterName: 'Target frequency [mHz]' },
  { parameterIndex: 2, parameterName: 'Target amplitude [permilles of rated torque]' },
  { parameterIndex: 3, parameterName: 'Transition time [ms]' },
  { parameterIndex: 4, parameterName: 'Signal type' },
  { parameterIndex: 5, parameterName: 'Start the procedure' },
];

export interface SystemIdentificationOsCommandResponse extends OsCommandResponse {
  systemIdentificationCompleted?: boolean;
}

export function createTriggerErrorOsCommand(firmwareService: number = 0, errorType: number = 11) {
  const elements = [OsCommandId.TRIGGER_ERROR, firmwareService, errorType];
  return createOsCommandTypedArray(elements);
}

export interface TriggerErrorOsCommandResponse extends OsCommandResponse {
  errorSet: boolean;
}

export const triggerErrorTypes = [
  { value: 4, name: 'RAISE ET_LOAD_STORE EXCEPTION' },
  { value: 6, name: 'RAISE ET_ARITHMETIC EXCEPTION' },
  { value: 7, name: 'RAISE ET_ECALL EXCEPTION' },
  { value: 10, name: 'HANG IN AN ENDLESS LOOP' },
  { value: 11, name: 'RAISE RESETTABLE FIRMWARE ERROR `DiagErr`' },
];

export enum KublerEncoderSubcommandId {
  WRITE_CORRECTION_TABLE = 0,
  SEND_SCPI_COMMAND = 1,
  SWITCH_TO_BOOTLOADER_FIRMWARE = 2,
  CHECK_FIRMWARE_UPDATE_SIZE = 3,
  ERASE_FIRMWARE = 4,
  WRITE_FIRMWARE = 5,
  EXIT_BOOTLOADER_MODE = 6,
  READ_CORRECTION_TABLE = 7,
}

export function createKublerEncoderOsCommand(subcommandId: KublerEncoderSubcommandId) {
  const elements = [OsCommandId.KUBLER_ENCODER_COMMAND, subcommandId];
  return createOsCommandTypedArray(elements);
}

export function createKublerEncoderCommandCheckFirmwareUpdateSizeOsCommand(firmwareSize: number) {
  let lsb: number = firmwareSize & 0xff;
  let lsb1: number = (firmwareSize >> 8) & 0xff;
  let lsb2: number = (firmwareSize >> 16) & 0xff;
  let msb: number = (firmwareSize >> 24) & 0xff;
  const elements = [
    OsCommandId.KUBLER_ENCODER_COMMAND,
    KublerEncoderSubcommandId.CHECK_FIRMWARE_UPDATE_SIZE,
    lsb,
    lsb1,
    lsb2,
    msb,
  ];
  return createOsCommandTypedArray(elements);
}

export function createKublerEncoderCommandWriteFirmwareOsCommand(firmwareSize: number) {
  let lsb: number = firmwareSize & 0xff;
  let lsb1: number = (firmwareSize >> 8) & 0xff;
  let lsb2: number = (firmwareSize >> 16) & 0xff;
  let msb: number = (firmwareSize >> 24) & 0xff;
  const elements = [OsCommandId.KUBLER_ENCODER_COMMAND, KublerEncoderSubcommandId.WRITE_FIRMWARE, lsb, lsb1, lsb2, msb];
  return createOsCommandTypedArray(elements);
}

export interface KublerEncoderCommandOsCommandResponse extends OsCommandResponse {
  numberOfBytesWritten?: number;
  responseInFoeBuffer?: boolean;
}

export const kublerEncoderCommandOsCommandErrors: OsCommandErrors = {
  0: {
    errorName: 'Async communication in progress',
    errorDescription:
      'The command is currently not allowed because another asynchronous encoder communication is already being executed.',
  },
  1: {
    errorName: 'Buffer timeout error',
    errorDescription: 'The FOE buffer was empty for more than 10 seconds.',
  },
  2: {
    errorName: 'Unknown command',
    errorDescription: 'The received command is not known.',
  },
  3: {
    errorName: 'Wrong data length',
    errorDescription:
      'The provided correction table is too short or too long. In any case, the firmware will ensure to write 1024 bytes to the encoder so that the correction table writing is finished.',
  },
  4: {
    errorName: 'Timeout',
    errorDescription: "The encoder didn't respond to the request after 10 ms.",
  },
  5: {
    errorName: 'Command not acknowledged',
    errorDescription: 'The encoder sent a NACK for the command (not acknowledge).',
  },
  6: {
    errorName: 'Command error received',
    errorDescription: 'The encoder sent a Command Error.',
  },
  7: {
    errorName: 'Firmware verification failed',
    errorDescription: 'The firmware was written to flash, but the encoder verification failed.',
  },
};

export function createUseInternalEncoderVelocityOsCommand(trigger: number = 0) {
  const elements = [OsCommandId.USE_INTERNAL_ENCODER_VELOCITY, trigger];
  return createOsCommandTypedArray(elements);
}

export interface UseInternalEncoderVelocityOsCommandResponse extends OsCommandResponse {
  usingInternalEncoderVelocity: boolean;
}

export function createKublerEncoderRegisterCommunicationOsCommand(
  rw: number = 0,
  registerAddress: number = 0,
  registerLength: number = 0,
  registerWriteValue: number = 0
) {
  let msb: number = (registerWriteValue >> 24) & 0xff;
  let msb1: number = (registerWriteValue >> 16) & 0xff;
  let msb2: number = (registerWriteValue >> 8) & 0xff;
  let lsb: number = registerWriteValue & 0xff;
  const elements = [
    OsCommandId.KUBLER_ENCODER_REGISTER_COMMUNICATION,
    rw,
    registerAddress,
    registerLength,
    lsb,
    msb2,
    msb1,
    msb,
  ];
  return createOsCommandTypedArray(elements);
}

export interface KublerEncoderRegisterCommunicationOsCommandResponse extends OsCommandResponse {
  kublerRegisterValue: number;
}

export const kublerEncoderRegisterCommunicationReadOsCommandErrors: OsCommandErrors = {
  0: {
    errorName: 'Async communication in progress',
    errorDescription:
      'The command is currently not allowed because another asynchronous encoder communication is already being executed.',
  },
  1: {
    errorName: 'Invalid register length',
    errorDescription: 'The register length selected is lower than 1 or higher than 4.',
  },
  2: {
    errorName: 'Encoder Timeout',
    errorDescription: "The encoder didn't return a register value after 10ms.",
  },
  3: {
    errorName: 'Wrong byte count',
    errorDescription:
      "The register value returned by the encoder doesn't have the same size as the register length specified.",
  },
};

export const kublerEncoderRegisterCommunicationWriteOsCommandErrors: OsCommandErrors = {
  0: {
    errorName: 'Async communication in progress',
    errorDescription:
      'The command is currently not allowed because another asynchronous encoder communication is already being executed.',
  },
  1: {
    errorName: 'Invalid register length',
    errorDescription: 'The register length selected is lower than 1 or higher than 4.',
  },
};

export const kublerEncoderRegisterValueMeaning: { [register: string]: { [value: string]: string } } = {
  36: {
    1: 'POA auto-calibration is not running.',
    25: 'POA auto-calibration reached good results but is still running at the moment.',
    17: 'POA auto-calibration reached good results and is finished, obtained values are saved automatically.',
    9: 'POA auto-calibration did not reach good results yet and is still running.',
  },
};

export interface OsCommand {
  id: OsCommandId;
  name: string;
}

export const osCommands: OsCommand[] = [
  { id: OsCommandId.CUSTOM_OS_COMMAND, name: 'Custom OS command' },
  { id: OsCommandId.ENCODER_REGISTER_COMMUNICATION, name: 'Encoder register communication' },
  { id: OsCommandId.ICMU_CALIBRATION_MODE, name: 'iC-MU calibration mode' },
  { id: OsCommandId.OPEN_LOOP_FIELD_MODE, name: 'Open Loop Field Mode (OLFM)' },
  { id: OsCommandId.HRD_STREAMING, name: 'HRD streaming' },
  { id: OsCommandId.MOTOR_PHASE_ORDER_DETECTION, name: 'Motor phase order detection' },
  { id: OsCommandId.COMMUTATION_OFFSET_MEASUREMENT, name: 'Commutation offset detection' },
  { id: OsCommandId.OPEN_PHASE_DETECTION, name: 'Open phase detection' },
  { id: OsCommandId.POLE_PAIR_DETECTION, name: 'Pole pair detection' },
  { id: OsCommandId.PHASE_RESISTANCE_MEASUREMENT, name: 'Phase resistance measurement' },
  { id: OsCommandId.PHASE_INDUCTANCE_MEASUREMENT, name: 'Phase inductance measurement' },
  { id: OsCommandId.TORQUE_CONSTANT_MEASUREMENT, name: 'Torque constant measurement' },
  { id: OsCommandId.SMM_ACYCLIC_HANDLER, name: 'SMM Acyclic Handler' },
  { id: OsCommandId.SKIPPED_CYCLED_COUNTER, name: 'Skipped cycles counter' },
  { id: OsCommandId.IGNORE_BISS_STATUS_BITS, name: 'Ignore BISS status bits' },
  { id: OsCommandId.SYSTEM_IDENTIFICATION, name: 'System identification' },
  { id: OsCommandId.TRIGGER_ERROR, name: 'Trigger error' },
  { id: OsCommandId.KUBLER_ENCODER_COMMAND, name: 'Kübler Encoder Command' },
  { id: OsCommandId.USE_INTERNAL_ENCODER_VELOCITY, name: 'Use Internal Encoder Velocity' },
  { id: OsCommandId.KUBLER_ENCODER_REGISTER_COMMUNICATION, name: 'Kübler Encoder Register Communication' },
];

export const smmAcyclicHandlerSubcommands: OsCommandSubcommand<SmmAcyclicHandlerSubcommandId>[] = [
  { id: SmmAcyclicHandlerSubcommandId.PARAMETER_DOWNLOAD_LOGIN, name: 'Parameter Download Login' },
  { id: SmmAcyclicHandlerSubcommandId.TRANSMIT_PARAMETERS, name: 'Transmit Parameters' },
  { id: SmmAcyclicHandlerSubcommandId.LOAD_PARAMETERS_FOR_VERIFICATION, name: 'Load Parameters for Verification' },
  { id: SmmAcyclicHandlerSubcommandId.VERIFY_PARAMETERS, name: 'Verify Parameters' },
  { id: SmmAcyclicHandlerSubcommandId.LOAD_VALIDATION_FILE, name: 'Load Validation File' },
  { id: SmmAcyclicHandlerSubcommandId.VALIDATE_CONFIGURATION, name: 'Validate Configuration' },
  { id: SmmAcyclicHandlerSubcommandId.CHANGE_PASSWORD, name: 'Change Password' },
  { id: SmmAcyclicHandlerSubcommandId.LOGOUT, name: 'Logout' },
  { id: SmmAcyclicHandlerSubcommandId.SOFTWARE_UPDATE_LOGIN, name: 'Software Update Login' },
  { id: SmmAcyclicHandlerSubcommandId.CONFIGURE_SOFTWARE_UPDATE, name: 'Configure Software Update' },
  { id: SmmAcyclicHandlerSubcommandId.TRANSMIT_SOFTWARE, name: 'Transmit Software' },
  { id: SmmAcyclicHandlerSubcommandId.FINALIZE_SOFTWARE_UPDATE, name: 'Finalize Software Update' },
  { id: SmmAcyclicHandlerSubcommandId.READ_SMM_PARAMETER, name: 'Read SMM Parameter' },
  { id: SmmAcyclicHandlerSubcommandId.IO_FAILURE_ACKNOWLEDGE, name: 'IO Failure Acknowledge' },
  { id: SmmAcyclicHandlerSubcommandId.TRIGGER_RESTART, name: 'Trigger Restart' },
];

export const kublerEncoderSubcommands: OsCommandSubcommand<KublerEncoderSubcommandId>[] = [
  { id: KublerEncoderSubcommandId.WRITE_CORRECTION_TABLE, name: 'Write Correction Table' },
  { id: KublerEncoderSubcommandId.SEND_SCPI_COMMAND, name: 'Send SCPI Command' },
  { id: KublerEncoderSubcommandId.SWITCH_TO_BOOTLOADER_FIRMWARE, name: 'Switch to Bootloader Firmware' },
  { id: KublerEncoderSubcommandId.CHECK_FIRMWARE_UPDATE_SIZE, name: 'Check Firmware Update Size' },
  { id: KublerEncoderSubcommandId.ERASE_FIRMWARE, name: 'Erase Firmware' },
  { id: KublerEncoderSubcommandId.WRITE_FIRMWARE, name: 'Write Firmware' },
  { id: KublerEncoderSubcommandId.EXIT_BOOTLOADER_MODE, name: 'Exit Bootloader Mode' },
  { id: KublerEncoderSubcommandId.READ_CORRECTION_TABLE, name: 'Read Correction Table' },
];

/**
 * Maps an OS command response to a specific object, effectively adding more
 * properties to the response object, such as the computed commutation angle offset.
 */
export function mapOsCommandResponse() {
  return pipe(
    map((response: OsCommandResponse | void) => {
      if (response && response.command) {
        const osCommandId = response.command[0] as OsCommandId;

        if (osCommandId === OsCommandId.ENCODER_REGISTER_COMMUNICATION) {
          if (response.request === 'succeeded' && response.data) {
            return { ...response, registerValue: response.data[0] };
          }
        }

        if (osCommandId === OsCommandId.ICMU_CALIBRATION_MODE) {
          if (response.request === 'succeeded') {
            return { ...response, calibrationModeSet: true };
          }
        }

        if (osCommandId === OsCommandId.OPEN_LOOP_FIELD_MODE) {
          if (response.request === 'succeeded') {
            return { ...response, openLoopFieldMode: true };
          }
        }

        if (osCommandId === OsCommandId.HRD_STREAMING) {
          if (response.request === 'succeeded') {
            return { ...response, hrdStreamingComplete: true };
          }

          if (response.request === 'failed') {
            if (typeof response.errorCode === 'number' && response.errorCode <= 250) {
              const { errorName, errorDescription } = hrdStreamingOsCommandErrors[response.errorCode];
              return { ...response, hrdStreamingComplete: false, errorName, errorDescription };
            }
            return { ...response, hrdStreamingComplete: false };
          }
        }

        if (osCommandId === OsCommandId.SMM_ACYCLIC_HANDLER) {
          if (response.request === 'failed' && typeof response.errorCode === 'number' && response.errorCode <= 250) {
            const error = smmAcyclicHandlerOsCommandErrors[response.errorCode];
            if (error) {
              return { ...response, errorName: error.errorName, errorDescription: error.errorDescription };
            }
          }
        }

        if (osCommandId === OsCommandId.MOTOR_PHASE_ORDER_DETECTION) {
          if (response.request === 'succeeded' && response.data) {
            return { ...response, motorPhaseOrder: response.data[0] };
          }
        }

        if (osCommandId === OsCommandId.COMMUTATION_OFFSET_MEASUREMENT) {
          if (response.request === 'succeeded' && response.data) {
            const [b0, b1] = response.data;
            const commutationAngleOffset = (b0 << 8) | b1;
            return { ...response, commutationAngleOffset };
          }
        }

        if (osCommandId === OsCommandId.OPEN_PHASE_DETECTION) {
          if (response.request === 'succeeded') {
            return { ...response, phaseOpened: false };
          }

          if (response.request === 'failed') {
            if (typeof response.errorCode === 'number' && response.errorCode <= 250) {
              const { errorName, errorDescription } = openPhaseDetectionErrors[response.errorCode];
              return { ...response, phaseOpened: true, errorName, errorDescription };
            }
            return { ...response, phaseOpened: true };
          }
        }

        if (osCommandId === OsCommandId.POLE_PAIR_DETECTION) {
          if (response.request === 'succeeded' && response.data) {
            return { ...response, numberOfPolePairs: response.data[0] };
          }

          if (response.request === 'failed') {
            if (typeof response.errorCode === 'number' && response.errorCode === 0) {
              return {
                ...response,
                errorName: 'Current amplitude error',
                errorDescription:
                  'The drive is not able to increase the amplitude of motor phase currents to the required value. This problem can occur due to limited Vdc or high motor phase impedances.',
              };
            }
          }
        }

        if (osCommandId === OsCommandId.PHASE_RESISTANCE_MEASUREMENT) {
          if (response.request === 'succeeded' && response.data) {
            const [b0, b1, b2, b3] = response.data;
            const phaseResistance = (b0 << 24) | (b1 << 16) | (b2 << 8) | b3;
            return { ...response, phaseResistance };
          }

          if (response.request === 'failed') {
            if (typeof response.errorCode === 'number' && response.errorCode === 0) {
              return {
                ...response,
                errorName: 'Current amplitude error',
                errorDescription: 'Unable to increase the current amplitude.',
              };
            }
          }
        }

        if (osCommandId === OsCommandId.PHASE_INDUCTANCE_MEASUREMENT) {
          if (response.request === 'succeeded' && response.data) {
            const [b0, b1, b2, b3] = response.data;
            const phaseInductance = (b0 << 24) | (b1 << 16) | (b2 << 8) | b3;
            return { ...response, phaseInductance };
          }

          if (response.request === 'failed') {
            if (typeof response.errorCode === 'number' && response.errorCode === 0) {
              return {
                ...response,
                errorName: 'Current amplitude error',
                errorDescription: 'Unable to increase the current amplitude.',
              };
            }
          }
        }

        if (osCommandId === OsCommandId.TORQUE_CONSTANT_MEASUREMENT) {
          if (response.request === 'succeeded' && response.data) {
            const [b0, b1, b2, b3] = response.data;
            const torqueConstant = (b0 << 24) | (b1 << 16) | (b2 << 8) | b3;
            return { ...response, torqueConstant };
          }
        }

        if (osCommandId === OsCommandId.SKIPPED_CYCLED_COUNTER) {
          if (response.request === 'succeeded' && response.data) {
            const [b0, b1, b2, b3] = response.data;
            const skippedCycles = (b0 << 24) | (b1 << 16) | (b2 << 8) | b3;
            return { ...response, skippedCycles };
          }
        }

        if (osCommandId === OsCommandId.IGNORE_BISS_STATUS_BITS) {
          if (response.request === 'succeeded') {
            return { ...response, ignoringBissStatusBits: true };
          }
        }

        if (osCommandId === OsCommandId.SYSTEM_IDENTIFICATION) {
          if (response.request === 'succeeded') {
            return { ...response, systemIdentificationCompleted: true };
          }
        }

        if (osCommandId === OsCommandId.TRIGGER_ERROR) {
          if (response.request === 'succeeded') {
            return { ...response, errorSet: true };
          }
        }

        if (osCommandId === OsCommandId.KUBLER_ENCODER_COMMAND) {
          if (response.request === 'succeeded' && response.data) {
            const numberOfBytesWritten = (response.data[0] << 8) | response.data[1];
            const responseInFoeBuffer = response.data[2];
            return { ...response, numberOfBytesWritten, responseInFoeBuffer };
          }

          if (response.request === 'failed') {
            if (typeof response.errorCode === 'number' && response.errorCode <= 250) {
              const { errorName, errorDescription } = kublerEncoderCommandOsCommandErrors[response.errorCode];
              return { ...response, errorName, errorDescription };
            }
          }
        }

        if (osCommandId === OsCommandId.USE_INTERNAL_ENCODER_VELOCITY) {
          if (response.request === 'succeeded') {
            return { ...response, usingInternalEncoderVelocity: true };
          }
        }

        if (osCommandId === OsCommandId.KUBLER_ENCODER_REGISTER_COMMUNICATION) {
          if (response.request === 'succeeded' && response.data) {
            const [b0, b1, b2, b3] = response.data;
            const kublerRegisterValue = b0 | (b1 << 8) | (b2 << 16) | (b3 << 24);
            return { ...response, kublerRegisterValue };
          }

          if (response.request === 'failed') {
            if (typeof response.errorCode === 'number' && response.errorCode <= 250) {
              const { errorName, errorDescription } =
                kublerEncoderRegisterCommunicationReadOsCommandErrors[response.errorCode];
              return { ...response, errorName, errorDescription };
            }
          }
        }
      }

      return response;
    })
  );
}
